export const POST_LOGIN_REDIRECT_STORAGE_KEY = "postLoginRedirect";
export const POST_LOGIN_REDIRECT_TTL_MS = 10 * 60 * 1000;
export const DEFAULT_POST_LOGIN_PATH = "/home";

type StoredPostLoginRedirect = {
  path: string;
  savedAt: number;
};

// Accept only relative /home paths (e.g. /home/talents/123). A shared link may be
// https://core.../home/..., but the browser already strips the origin before we
// save. Rejecting :// prevents navigate() from being pointed at another site.
export const isSafePostLoginPath = (path: string): boolean => {
  if (path.includes("\\") || path.includes("://")) return false;
  if (!path.startsWith("/home")) return false;
  if (path.startsWith("//")) return false;
  if (path.length > 5) {
    const next = path[5];
    if (next !== "/" && next !== "?" && next !== "#") return false;
  }
  return true;
};

const currentLocationPath = (): string =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

const readStoredRedirect = (): StoredPostLoginRedirect | null => {
  try {
    const raw = sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPostLoginRedirect;
    if (
      typeof parsed?.path !== "string" ||
      typeof parsed?.savedAt !== "number"
    ) {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const savePostLoginRedirect = (from?: string): void => {
  const path = from ?? currentLocationPath();
  if (!isSafePostLoginPath(path)) return;
  try {
    const payload: StoredPostLoginRedirect = {
      path,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(
      POST_LOGIN_REDIRECT_STORAGE_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // sessionStorage may be unavailable
  }
};

export const clearPostLoginRedirect = (): void => {
  try {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable
  }
};

export const consumePostLoginRedirect = (): string | null => {
  const stored = readStoredRedirect();
  // Drop immediately so a later login cannot reuse this path.
  clearPostLoginRedirect();
  if (!stored) return null;
  if (Date.now() - stored.savedAt > POST_LOGIN_REDIRECT_TTL_MS) return null;
  if (!isSafePostLoginPath(stored.path)) return null;
  return stored.path;
};

export const resolvePostLoginPath = (): string =>
  consumePostLoginRedirect() ?? DEFAULT_POST_LOGIN_PATH;
